/**
 * Deterministic file/folder selection model shared by every selection surface.
 *
 * The selected file IDs are the single source of truth. Folder states are
 * always derived from them, so a folder can never disagree with its contents
 * and no state has to be recalculated by hand when navigating the tree.
 */

export type FolderSelectionState = 'full' | 'partial' | 'none';

export interface FolderDescendants {
  /** Every folder below this folder, at any depth. */
  folderIds: string[];
  /** Every selectable file below this folder, at any depth. */
  fileIds: string[];
}

/** Resolved descendants per folder ID. */
export type DescendantsCache = Map<string, FolderDescendants>;

export interface SelectionSnapshot {
  folderIds: string[];
  fileIds: string[];
}

export const normalizeId = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'object') {
    return normalizeId((value as { id?: unknown }).id);
  }

  const id = String(value).trim();

  return id === '' ? null : id;
};

/**
 * Extract IDs from an inquiry's `folders`/`files` array, which may contain
 * plain IDs or full objects depending on the endpoint.
 */
export const toIdList = (items: unknown): string[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const ids = new Set<string>();
  items.forEach(item => {
    const id = normalizeId(item);
    if (id !== null) {
      ids.add(id);
    }
  });

  return [...ids];
};

/**
 * A folder is fully selected when every selectable descendant file is
 * selected, partially selected when only some are, and not selected otherwise.
 *
 * Folders with no selectable descendants — empty folders, or folders holding
 * only empty subfolders — are never reported as partially selected.
 * Folders whose descendants are not resolved yet report `none`.
 */
export const getFolderSelectionState = (
  folderId: string,
  selectedFileIds: ReadonlySet<string>,
  descendants: DescendantsCache,
): FolderSelectionState => {
  const resolved = descendants.get(folderId);

  if (!resolved || resolved.fileIds.length === 0) {
    return 'none';
  }

  let selectedCount = 0;
  resolved.fileIds.forEach(fileId => {
    if (selectedFileIds.has(fileId)) {
      selectedCount += 1;
    }
  });

  if (selectedCount === 0) {
    return 'none';
  }

  return selectedCount === resolved.fileIds.length ? 'full' : 'partial';
};

export const applyFileSelection = (
  selectedFileIds: ReadonlySet<string>,
  fileId: string,
  shouldSelect: boolean,
): Set<string> => {
  const next = new Set(selectedFileIds);

  if (shouldSelect) {
    next.add(fileId);
  } else {
    next.delete(fileId);
  }

  return next;
};

/**
 * Selecting a folder selects every selectable descendant file; deselecting it
 * removes them. Ancestor and descendant folder states follow automatically
 * because they are derived from the same file set.
 */
export const applyFolderSelection = (
  selectedFileIds: ReadonlySet<string>,
  folderId: string,
  shouldSelect: boolean,
  descendants: DescendantsCache,
): Set<string> => {
  const resolved = descendants.get(folderId);

  if (!resolved) {
    return new Set(selectedFileIds);
  }

  const next = new Set(selectedFileIds);
  resolved.fileIds.forEach(fileId => {
    if (shouldSelect) {
      next.add(fileId);
    } else {
      next.delete(fileId);
    }
  });

  return next;
};

/**
 * Build the folder ID list to persist alongside the selected files.
 *
 * Folders whose descendants are known contribute only when fully selected.
 * Folders that were persisted earlier but have not been resolved in this
 * session are preserved, so browsing one branch never discards the recorded
 * state of another.
 */
export const computePersistedFolderIds = (
  selectedFileIds: ReadonlySet<string>,
  descendants: DescendantsCache,
  previouslyPersistedFolderIds: readonly string[],
): string[] => {
  const folderIds = new Set(
    previouslyPersistedFolderIds.filter(folderId => !descendants.has(folderId)),
  );

  descendants.forEach((_, folderId) => {
    if (
      getFolderSelectionState(folderId, selectedFileIds, descendants) === 'full'
    ) {
      folderIds.add(folderId);
    }
  });

  return [...folderIds];
};

/**
 * Reconcile a local selection with a newer server selection by replaying the
 * local changes on top of it, so a change made elsewhere and a change made
 * here both survive instead of one silently overwriting the other.
 */
export const mergeSelections = (
  serverFileIds: readonly string[],
  lastPersistedFileIds: ReadonlySet<string>,
  localFileIds: ReadonlySet<string>,
): Set<string> => {
  const merged = new Set(serverFileIds);

  localFileIds.forEach(fileId => {
    if (!lastPersistedFileIds.has(fileId)) {
      merged.add(fileId);
    }
  });

  lastPersistedFileIds.forEach(fileId => {
    if (!localFileIds.has(fileId)) {
      merged.delete(fileId);
    }
  });

  return merged;
};

export const isSameIdList = (
  left: readonly string[],
  right: readonly string[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const rightSet = new Set(right);

  return left.every(id => rightSet.has(id));
};

export const isSameSnapshot = (
  left: SelectionSnapshot,
  right: SelectionSnapshot,
): boolean =>
  isSameIdList(left.fileIds, right.fileIds) &&
  isSameIdList(left.folderIds, right.folderIds);

/** Split IDs into request-sized batches. */
export const chunkIds = (ids: readonly string[], size: number): string[][] => {
  if (size <= 0) {
    return [[...ids]];
  }

  const batches: string[][] = [];
  for (let index = 0; index < ids.length; index += size) {
    batches.push(ids.slice(index, index + size));
  }

  return batches;
};

/** A record in the folder table is a file when it is a leaf or carries a type. */
export const isFileRecord = (record: any): boolean =>
  record?.isLeaf === true || Boolean(record?.type && record.type !== '');
